import { tKey, type I18nKey } from "@/core/i18n";

export interface HyperparamsPreset {
  id: string;
  name: I18nKey<"model">;
  description: I18nKey<"model">;
  /** Interpolated into `description` (e.g. the per-task resolution). */
  descriptionParams?: Record<string, string | number>;
  config: Record<string, unknown>;
}

// Distilled from training-configs/ultralytics-hyperparams*.json runs.
// `batch` and `epochs` are intentionally omitted — they're controlled by the
// form fields and pulled from training_config in ultralytics_config.py.
const ULTRALYTICS_PRESETS: HyperparamsPreset[] = [
  {
    id: "fast-training",
    name: tKey("model:presets.names.fastTraining"),
    description: tKey("model:presets.ultralytics.fastTraining"),
    config: {
      model_size: "nano",
      imgsz: 640,
      optimizer: "AdamW",
      lr0: 0.001,
      cos_lr: true,
      patience: 20,
    },
  },
  {
    id: "high-accuracy",
    name: tKey("model:presets.names.highAccuracy"),
    description: tKey("model:presets.ultralytics.highAccuracy"),
    config: {
      model_size: "large",
      imgsz: 1280,
      optimizer: "AdamW",
      lr0: 0.001,
      lrf: 0.01,
      momentum: 0.937,
      weight_decay: 0.0005,
      cos_lr: true,
      warmup_epochs: 5,
      warmup_momentum: 0.8,
      warmup_bias_lr: 0.1,
      patience: 50,
      box: 7.5,
      cls: 0.5,
      dfl: 1.5,
      mosaic: 1.0,
      close_mosaic: 15,
      mixup: 0.15,
      copy_paste: 0.1,
      hsv_h: 0.015,
      hsv_s: 0.7,
      hsv_v: 0.4,
      translate: 0.1,
      scale: 0.5,
      fliplr: 0.5,
      amp: true,
    },
  },
  {
    id: "low-memory",
    name: tKey("model:presets.names.lowMemory"),
    description: tKey("model:presets.ultralytics.lowMemory"),
    config: {
      model_size: "nano",
      imgsz: 640,
      workers: 4,
      optimizer: "AdamW",
      lr0: 0.001,
      cos_lr: true,
      patience: 30,
    },
  },
];

// No per-task rewriting needed: `model_size` names a capacity, and the
// training service resolves the task-specific weights from the model type.
export const getHyperparamsPresets = (): HyperparamsPreset[] =>
  ULTRALYTICS_PRESETS;
